import type {
  SchoolEntity,
  SchoolInputEntity,
  SchoolMeEntity
} from '@/core/infrastructure/schemas/school/core/SchoolSchema'
import {
  SchoolSchema,
  SchoolInputSchema,
  SchoolMeSchema
} from '@/core/infrastructure/schemas/school/core/SchoolSchema'
import SchoolClassroom from '@/core/domain/school/classroom/SchoolClassroom'
import slugify from 'slugify'
import { SchoolPeriodUser } from '@/core/domain/school/user/SchoolPeriodUser'
import SchoolYear from '@/core/domain/school/year/SchoolYear'
import { EntityValidator } from '@/core/infrastructure/utils/EntityValidator'
import { SchoolType } from '@/core/domain/resources/enums/SchoolType.ts'

/**
 * School domain class
 */

export default class School {
  readonly id: number
  readonly name: string
  readonly codeUai: string
  readonly address: string | null
  readonly city: string
  readonly zipCode: string
  readonly codeInternal: string
  readonly department: string | null
  readonly schoolDistrict: string | null
  readonly contactEmail: string | null
  readonly schoolType?: SchoolType.Values | null
  readonly slug: string

  constructor(school: SchoolEntity) {
    this.id = school.id
    this.name = school.name
    this.codeUai = school.codeUai
    this.address = school.address
    this.city = school.city
    this.zipCode = school.zipCode
    this.codeInternal = school.codeInternal
    this.department = school.department
    this.schoolDistrict = school.schoolDistrict
    this.contactEmail = school.contactEmail
    this.schoolType = school.schoolType
    this.slug = slugify(school.name, {
      lower: true,
      strict: true,
      locale: 'fr',
      remove: /[*+~.()'"!:@]/g
    })
  }

  get school() {
    return {
      id: this.id,
      name: this.name,
      codeUai: this.codeUai,
      address: this.address,
      city: this.city,
      zipCode: this.zipCode,
      codeInternal: this.codeInternal,
      department: this.department,
      schoolDistrict: this.schoolDistrict,
      contactEmail: this.contactEmail,
      schoolType: this.schoolType
    }
  }

  toInput() {
    return new SchoolInput({
      id: this.id,
      name: this.name,
      codeUai: this.codeUai,
      address: this.address,
      city: this.city,
      zipCode: this.zipCode,
      department: this.department,
      schoolDistrict: this.schoolDistrict,
      contactEmail: this.contactEmail,
      schoolType: this.schoolType
    })
  }

  /**
   * Crée une instance de School à partir de données brutes
   */
  public static create(data: unknown): School {
    return EntityValidator.validateAndCreate(SchoolSchema, data, School, 'School')
  }
}

export class SchoolMe extends School {
  readonly classrooms: SchoolClassroom[]
  readonly users: SchoolPeriodUser[]
  readonly schoolYear: SchoolYear
  readonly schoolPeriodId: number

  constructor(school: SchoolMeEntity) {
    super(school)
    this.classrooms = school.classrooms.map((classroom) => SchoolClassroom.create(classroom))
    this.users = school.users.map((user) => SchoolPeriodUser.create(user))
    this.schoolYear = SchoolYear.create(school.schoolYear)
    this.schoolPeriodId = school.schoolPeriodId
  }

  get school() {
    return {
      id: this.id,
      name: this.name,
      codeUai: this.codeUai,
      address: this.address,
      city: this.city,
      zipCode: this.zipCode,
      codeInternal: this.codeInternal,
      department: this.department,
      schoolDistrict: this.schoolDistrict,
      contactEmail: this.contactEmail,
      schoolType: this.schoolType,
      classrooms: this.classrooms,
      users: this.users,
      schoolYear: this.schoolYear,
      schoolPeriodId: this.schoolPeriodId
    }
  }

  /**
   * Crée une instance de SchoolMe à partir de données brutes
   */
  public static create(data: unknown): SchoolMe {
    return EntityValidator.validateAndCreate(SchoolMeSchema, data, SchoolMe, 'SchoolMe')
  }
}

export class SchoolInput {
  schoolId?: number
  name: string
  codeUai: string
  address: string | null
  city: string
  zipCode: string
  department: string | null
  schoolDistrict: string | null
  contactEmail: string | null
  schoolType?: SchoolType.Values | null

  constructor(schoolInput?: SchoolInputEntity) {
    this.schoolId = schoolInput?.id || undefined
    this.name = schoolInput?.name || ''
    this.codeUai = schoolInput?.codeUai || ''
    this.address = schoolInput?.address || null
    this.city = schoolInput?.city || ''
    this.zipCode = schoolInput?.zipCode || ''
    this.department = schoolInput?.department || null
    this.schoolDistrict = schoolInput?.schoolDistrict || null
    this.contactEmail = schoolInput?.contactEmail || null
    this.schoolType = schoolInput?.schoolType || null
  }

  get schoolInput() {
    return {
      schoolId: this.schoolId,
      name: this.name,
      codeUai: this.codeUai,
      address: this.address,
      city: this.city,
      zipCode: this.zipCode,
      department: this.department,
      schoolDistrict: this.schoolDistrict,
      contactEmail: this.contactEmail,
      schoolType: this.schoolType
    }
  }

  /**
   * Crée une instance de SchoolInput à partir de données brutes
   */
  public static create(data: unknown): SchoolInput {
    return EntityValidator.validateAndCreate(SchoolInputSchema, data, SchoolInput, 'SchoolInput')
  }
}
